import * as XLSX from 'xlsx';
import {
  getProductImportCategoryNames,
  PRODUCT_IMPORT_COLUMNS,
  PRODUCT_IMPORT_SHEET_NAME,
} from './productImportTemplate';

const DESC =
  'ไทลินอล ชนิดเม็ด มีตัวยาสำคัญ Paracetamol 500 mg ใช้สำหรับบรรเทาอาการปวดเล็กน้อยถึงปานกลาง';

export const PRODUCT_IMPORT_TEST_CASE_SECTION = 'test case file';

function headers() {
  return PRODUCT_IMPORT_COLUMNS.map((column) => column.header);
}

function row({
  barcode,
  category,
  nameTh,
  nameEn,
  desc = DESC,
  width = '5',
  length = '10',
  height = '8',
  weight = '15',
}) {
  return [barcode, category, nameTh, nameEn, desc, width, length, height, weight];
}

function validPair(category, suffix, barcode) {
  return row({
    barcode,
    category,
    nameTh: `นำเข้าทดสอบสำเร็จ ${suffix}`,
    nameEn: `Import Test Success ${suffix}`,
  });
}

export const PRODUCT_IMPORT_TEST_CASES = [
  {
    id: 'success',
    label: 'success',
    description: '2 แถวครบถ้วน ชื่อไทยและบาร์โค้ดไม่ซ้ำ หมวดมีในระบบ',
    expected: 'นำเข้าได้ทั้งหมด',
    build({ categoryName }) {
      return {
        filename: 'meya-import-tc-success.xlsx',
        sheet: [
          headers(),
          validPair(categoryName, 'หนึ่ง', '8851999000001'),
          validPair(categoryName, 'สอง', '8851999000002'),
        ],
      };
    },
  },
  {
    id: 'duplicated-thai-name',
    label: 'duplicated thai name',
    description: 'แถว 2 และ 3 ใช้ชื่อไทยเดียวกัน บาร์โค้ดคนละค่า',
    expected: 'fail ทั้งไฟล์ — ชื่อไทยซ้ำในไฟล์ (กติกาเดียวกับ item/add)',
    build({ categoryName }) {
      const nameTh = 'นำเข้าทดสอบชื่อไทยซ้ำในไฟล์';
      return {
        filename: 'meya-import-tc-duplicated-thai-name.xlsx',
        sheet: [
          headers(),
          row({
            barcode: '8851999000101',
            category: categoryName,
            nameTh,
            nameEn: 'Import Dup Thai Name A',
          }),
          row({
            barcode: '8851999000102',
            category: categoryName,
            nameTh,
            nameEn: 'Import Dup Thai Name B',
          }),
        ],
      };
    },
  },
  {
    id: 'duplicated-barcode',
    label: 'duplicated barcode',
    description: 'แถว 2 และ 3 ใช้บาร์โค้ดเดียวกัน ชื่อไทยคนละค่า',
    expected: 'item/add วันนี้ยอมให้บาร์โค้ดซ้ำ — import ควรถือว่าผ่านถ้าตามกติกาเดียวกัน',
    build({ categoryName }) {
      const barcode = '8851999000201';
      return {
        filename: 'meya-import-tc-duplicated-barcode.xlsx',
        sheet: [
          headers(),
          row({
            barcode,
            category: categoryName,
            nameTh: 'นำเข้าทดสอบบาร์โค้ดซ้ำ หนึ่ง',
            nameEn: 'Import Dup Barcode One',
          }),
          row({
            barcode,
            category: categoryName,
            nameTh: 'นำเข้าทดสอบบาร์โค้ดซ้ำ สอง',
            nameEn: 'Import Dup Barcode Two',
          }),
        ],
      };
    },
  },
  {
    id: 'duplicated-existing-record',
    label: 'duplicated existed record',
    description: 'แถวข้อมูลใช้ชื่อไทยของสินค้าที่มีในระบบ (บาร์โค้ดใหม่)',
    expected: 'fail — ชื่อไทยซ้ำกับของที่มีอยู่',
    needsExistingProduct: true,
    build({ categoryName, existingProduct }) {
      return {
        filename: 'meya-import-tc-duplicated-existing-record.xlsx',
        sheet: [
          headers(),
          row({
            barcode: '8851999000301',
            category: categoryName,
            nameTh: existingProduct.productitem_name,
            nameEn: existingProduct.productitem_inter || 'Import Existing Thai Name',
          }),
        ],
      };
    },
  },
  {
    id: 'empty-required',
    label: 'empty required fields',
    description: 'แถวละหนึ่งฟิลด์บังคับว่าง: บาร์โค้ด / หมวด / ชื่อไทย / ชื่ออังกฤษ / รายละเอียด',
    expected: 'fail ทั้งไฟล์ — type ว่าง',
    build({ categoryName }) {
      const base = {
        barcode: '8851999000401',
        category: categoryName,
        nameTh: 'นำเข้าทดสอบฟิลด์ว่าง หนึ่ง',
        nameEn: 'Import Empty Required',
      };
      return {
        filename: 'meya-import-tc-empty-required.xlsx',
        sheet: [
          headers(),
          row({ ...base, barcode: '' }),
          row({ ...base, barcode: '8851999000402', category: '' }),
          row({ ...base, barcode: '8851999000403', nameTh: '' }),
          row({ ...base, barcode: '8851999000404', nameEn: '' }),
          row({ ...base, barcode: '8851999000405', desc: '' }),
        ],
      };
    },
  },
  {
    id: 'invalid-format',
    label: 'invalid format',
    description: 'บาร์โค้ด/ชื่อสั้นกว่า 5, ขนาดไม่ใช่ตัวเลข, น้ำหนัก 0, หมวดที่ไม่มีในระบบ',
    expected: 'fail ทั้งไฟล์ — type รูปแบบ / ว่าง',
    build({ categoryName }) {
      return {
        filename: 'meya-import-tc-invalid-format.xlsx',
        sheet: [
          headers(),
          row({
            barcode: '123',
            category: categoryName,
            nameTh: 'นำเข้าทดสอบบาร์โค้ดสั้น',
            nameEn: 'Import Short Barcode',
          }),
          row({
            barcode: '8851999000502',
            category: categoryName,
            nameTh: 'สั้น',
            nameEn: 'Import Short Thai',
          }),
          row({
            barcode: '8851999000503',
            category: categoryName,
            nameTh: 'นำเข้าทดสอบความกว้างผิด',
            nameEn: 'Import Bad Width',
            width: 'abc',
          }),
          row({
            barcode: '8851999000504',
            category: categoryName,
            nameTh: 'นำเข้าทดสอบน้ำหนักศูนย์',
            nameEn: 'Import Zero Weight',
            weight: '0',
          }),
          row({
            barcode: '8851999000505',
            category: 'หมวดที่ไม่มีในระบบ-นำเข้าทดสอบ',
            nameTh: 'นำเข้าทดสอบหมวดไม่มี',
            nameEn: 'Import Unknown Category',
          }),
        ],
      };
    },
  },
  {
    id: 'wrong-headers',
    label: 'wrong headers',
    description: 'ไม่มีคอลัมน์ชื่อสินค้าภาษาไทย และมีคอลัมน์รหัสสินค้าที่ template ไม่ใช้',
    expected: 'fail ที่ขั้น validate header — ยังไม่อ่านแถวข้อมูล',
    build() {
      return {
        filename: 'meya-import-tc-wrong-headers.xlsx',
        sheet: [
          ['รหัสบาร์โค้ด', 'หมวดหมู่สินค้า', 'รหัสสินค้า', 'ชื่อสินค้าภาษาอังกฤษ', 'น้ำหนัก'],
          ['8851999000601', 'ยาสามัญประจำบ้าน', 'PI-000001', 'Import Wrong Headers', '15'],
        ],
      };
    },
  },
  {
    id: 'success-1000',
    label: 'success 1000 rows',
    description: 'หัวคอลัมน์ครบ + 1,000 แถวข้อมูล ชื่อไทยและบาร์โค้ดไม่ซ้ำ หมวดมีในระบบ',
    expected: 'นำเข้าได้ทั้งหมด (ทดสอบ bulk / poll)',
    build({ categoryName }) {
      const sheet = [headers()];
      for (let i = 1; i <= 1000; i += 1) {
        const n = String(i).padStart(4, '0');
        sheet.push(
          row({
            barcode: `88519991${n}`,
            category: categoryName,
            nameTh: `นำเข้าทดสอบจำนวนมาก ${n}`,
            nameEn: `Import Bulk Volume ${n}`,
          }),
        );
      }
      return {
        filename: 'meya-import-tc-success-1000.xlsx',
        sheet,
      };
    },
  },
  {
    id: 'headers-only',
    label: 'headers only',
    description: 'มีเฉพาะหัวคอลัมน์ครบ 9 คอลัมน์ ไม่มีแถวข้อมูล',
    expected: 'fail — ไม่มีแถวข้อมูล',
    build() {
      return {
        filename: 'meya-import-tc-headers-only.xlsx',
        sheet: [headers()],
      };
    },
  },
];

export function getExistingProductForImportTest(existingProducts = []) {
  return (existingProducts || []).find(
    (product) => (product?.productitem_name || '').trim().length >= 5,
  );
}

export function buildProductImportTestCaseXlsx(
  testCase,
  { categories = [], existingProducts = [] } = {},
) {
  const categoryNames = getProductImportCategoryNames(categories);
  const categoryName = categoryNames[0] || PRODUCT_IMPORT_COLUMNS[1].sample;
  const existingProduct = getExistingProductForImportTest(existingProducts);

  if (testCase.needsExistingProduct && !existingProduct) {
    throw new Error('ไม่มีสินค้าในรายการสำหรับเคสซ้ำกับของที่มีอยู่');
  }

  const { filename, sheet } = testCase.build({ categoryName, existingProduct });
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(sheet);
  worksheet['!cols'] = (sheet[0] || []).map((header) => ({
    wch: Math.max(16, String(header).length + 6),
  }));
  XLSX.utils.book_append_sheet(workbook, worksheet, PRODUCT_IMPORT_SHEET_NAME);
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return { filename, buffer };
}
